<template>
  <div class="nav-bar" role="menu" tabindex="0" ref="menu">
    <navigation-menu-header :close-modal="closeModal" />
    <div class="promotion-banner">
      <img v-if="promotion" :src="`/images/promotions/${promotion.imgName}`"
           @click="openPromotion" @keyup.enter="openPromotion" tabindex="0" :alt="promotion.description"/>
    </div>
    <navigation-menu-item class="margin-top" title="תזרים חודשי" v-on:navigate="closeModal" target="sr" icon-name="database"/>
    <balance-page-menu-item v-on:navigate="closeModal"/>
    <navigation-menu-item  icon-name="amp_stories"
                           title="הרייזאפ היומי"
                           v-on:navigate="openDailyRoutine" target="home/current"
                           segmentEventSuffix="dr-home">
      <div class="small-circle" v-if="dailyRoutines.length">{{ dailyRoutines.length }}</div>
    </navigation-menu-item>
    <navigation-menu-item  v-if="showMortgagePage" icon-name="home"
                           title="משכנתא עם רייזאפ"
                           v-on:navigate="closeModal" target="mortgage"
                           segmentEventSuffix="mortgage" />
    <navigation-menu-item  v-if="showIBIInvestmentsPage" icon-name="finance"
                           title="השקעות עם IBI"
                           v-on:navigate="closeModal" target="ibi-investments"
                           segmentEventSuffix="ibi-investments"/>
    <navigation-menu-item  v-if="showTaxReturnPage" icon-name="export_notes"
                           title="מגיע לי החזר מס?"
                           v-on:navigate="closeModal" target="tax-return"
                           segmentEventSuffix="tax-return" show-new-badge/>
    <navigation-menu-item  v-if="showMortgageResultsPage" icon-name="finance"
                           title="כמה משכנתא אני יכול?"
                           v-on:navigate="closeModal" target="mortgage-results"
                           segmentEventSuffix="mortgage-results" show-new-badge/>
    <navigation-menu-item  v-if="!preventPayments" icon-name="local_grocery_store" title="תווי קניה"
                          v-on:navigate="closeModal" :target="vouchersTarget"
                          :disabled="!customerStateIsActive"
                          segmentEventSuffix="SuperDiscount" />
    <navigation-menu-item v-if="showExperimentalPages" title="רעיונות בפיתוח" v-on:navigate="closeModal"
                          target="experimental-pages" icon-name="lightbulb"/>
    <navigation-menu-dropdown title="כלים" icon-name="chart_data">
      <div class="last-child-no-margin">
        <navigation-menu-item title="ההתקדמות שלי" :small="true"
                              v-on:navigate="closeModal" target="my-progress" :disabled="!customerStateIsActive"
                              segmentEventSuffix="MyProgress"/>
        <navigation-menu-item title="כמה עלה לי?" v-on:navigate="closeModal" target="stories" :small="true"/>
        <navigation-menu-item title="מה יקרה אם...?" v-on:navigate="closeModal" target="planner" :small="true" show-beta-badge />
        <plan-ahead-menu-item v-on:navigate="goToPlanAheadPage"/>
      </div>
    </navigation-menu-dropdown>
    <navigation-menu-dropdown title="חיסכון עם רייזאפ" icon-name="potted_plant">
      <div class="last-child-no-margin">
        <saving-zone-menu-item v-on:navigate="closeModal"/>
        <balance-money-menu-item v-on:navigate="closeModal" target-path="balance-money"/>
      </div>
    </navigation-menu-dropdown>
    <div class="tiles-grid">
      <menu-tile v-for="tile in menuTiles" v-bind="tile" :key="tile.text" @click="tile.onClick"/>
    </div>
  </div>
</template>

<script>
import BaseUI from '@riseupil/base-ui';
import { mapGetters, mapActions, mapState, mapMutations } from 'vuex';
import PlanAheadIntroductionPopup
  from '@/pages/responsive-pages/authenticated/navigation-pages/plan-ahead/PlanAheadIntroductionPopup';
import MenuTile from '@/base-components/app-header/MenuTile';
import PushNotificationPermissionPopup
  from '@/pages/responsive-pages/authenticated/components/PushNotificationPermissionPopup';
import { isAndroid, isIOS, isNativeApp, getPlatformInfo, getAppVersionNumber } from '@/utils/deviceUtils';
import router, { externalLinkRedirect } from '@/router';
import BalanceMoneyMenuItem from './navigation-menu-items/BalanceMoneyMenuItem';
import BalancePageMenuItem from './navigation-menu-items/BalancePageMenuItem.vue';
import SavingZoneMenuItem from './navigation-menu-items/SavingZoneMenuItem';
import PlanAheadMenuItem from './navigation-menu-items/PlanAheadMenuItem';
import Intercom from '../../Intercom';
import Segment from '../../Segment';
import NavigationMenuItem from './NavigationMenuItem.vue';
import NavigationMenuHeader from './NavigationMenuHeader';
import NavigationMenuDropdown from './NavigationMenuDropdown';

export default {
  name: 'NavigationMenu',
  components: {
    MenuTile,
    SavingZoneMenuItem,
    BalanceMoneyMenuItem,
    BalancePageMenuItem,
    NavigationMenuItem,
    PlanAheadMenuItem,
    NavigationMenuHeader,
    NavigationMenuDropdown,
  },
  props: {
    closeModal: {
      type: Function,
      required: true,
    },
  },
  computed: {
    ...mapGetters(
      'featureFlags',
      ['showExperimentalPages',
        'showNewVouchersFlow',
        'eoyMenuBannerSection',
        'preventPayments',
        'showMortgagePage',
        'newAppBanner',
        'newIosAppBanner',
        'showIBIInvestmentsPage',
        'showTaxReturnPage',
        'showMortgageResultsPage',
      ],
    ),
    ...mapGetters('planAhead', ['hasPlans']),
    ...mapGetters('advisory', ['advisoryFlowSharedUTMParams']),
    ...mapGetters('referrals', ['isDuringCompetition']),
    ...mapGetters('subscriptionState', ['hasAccessToCashflow']),
    ...mapState('applicationState', ['cashflowState']),
    ...mapGetters('applicationState', ['cashflowTypes']),
    ...mapState('dailyRoutines', ['dailyRoutines']),
    ...mapGetters('pushNotifications', ['hasPushTokens']),
    ...mapState('pushNotifications', { pushNotificationsInitialized: 'initialized' }),
    menuTiles() {
      if (this.preventPayments) {
        return [
          {
            text: 'צ׳אט עם הצוות',
            iconProps: {
              iconName: 'chat_bubble',
              fill: false,
              color: BaseUI.Colors.riseupBlue,
            },
            onClick: this.openChat,
          },
          {
            text: 'לקהילת הפייסבוק',
            iconProps: {
              iconName: 'diversity_1',
              fill: false,
              color: BaseUI.Colors.riseupDarkRed,
            },
            onClick: this.goToFacebook,
          },
          {
            text: 'מרכז המידע',
            iconProps: {
              iconName: 'quick_reference_all',
              fill: false,
              color: BaseUI.Colors.riseupGreen,
            },
            onClick: this.openFaq,
          }];
      }

      return [
        {
          text: 'צ׳אט עם הצוות',
          iconProps: {
            iconName: 'chat_bubble',
            fill: false,
            color: BaseUI.Colors.riseupBlue,
          },
          onClick: this.openChat,
        },
        {
          text: 'לקהילת הפייסבוק',
          iconProps: {
            iconName: 'diversity_1',
            fill: false,
            color: BaseUI.Colors.riseupDarkRed,
          },
          onClick: this.goToFacebook,
        },
        {
          text: 'מרכז המידע',
          iconProps: {
            iconName: 'quick_reference_all',
            fill: false,
            color: BaseUI.Colors.riseupGreen,
          },
          onClick: this.openFaq,
        },
        {
          text: 'להמליץ ולהרוויח',
          iconProps: {
            iconName: 'pages',
            fill: false,
            color: BaseUI.Colors.riseupLightSaturatedRed,
          },
          onClick: this.goToAmbassadorsFromNavigationMenu,
        },
      ];
    },
    promotion() {
      const { appVersion } = getPlatformInfo();
      const versionCheck = getAppVersionNumber(appVersion) >= 1.24;
      if (isNativeApp() && versionCheck && this.hasAccessToCashflow && this.pushNotificationsInitialized && !this.hasPushTokens) {
        return {
          key: 'push-notifications-banner',
          description: 'הפעלת התראות',
          imgName: 'push-notifications.svg',
          onClick: () => this.openPushNotificationPopup(),
        };
      }
      if (this.newAppBanner && isAndroid() && !isNativeApp()) {
        return {
          key: 'new-app-banner',
          description: 'מהיום רייזאפ גם באפליקציה!',
          imgName: 'android_app_banner.svg',
          onClick: () => this.goToNewApp(),
        };
      }
      if (this.newIosAppBanner && isIOS() && !isNativeApp()) {
        return {
          key: 'new-ios-app-banner',
          description: 'מהיום רייזאפ גם באפליקציה!',
          imgName: 'ios_app_banner.svg',
          onClick: () => this.goToNewApp(),
        };
      }
      if (!this.preventPayments) {
        return {
          key: 'ambassadors-menu-banner',
          description: 'לפרגן במינוי בהנחה ולהרוויח על הדרך',
          imgName: 'ambassador_banner.svg',
          onClick: () => this.goToAmbassadors(),
        };
      }

      return undefined;
    },
    customerStateIsActive() {
      return this.hasAccessToCashflow
          && !this.cashflowOutOfDate
          && !this.cashflowBlocked;
    },
    cashflowOutOfDate() {
      return this.cashflowState === this.cashflowTypes.OUT_OF_DATE;
    },
    cashflowBlocked() {
      return this.cashflowState === this.cashflowTypes.BLOCKED;
    },
    vouchersTarget() {
      if (this.showNewVouchersFlow) {
        return 'super-discount-v2/all';
      }
      return 'super-discount';
    },
  },
  created() {
    if (this.promotion) {
      Segment.trackUserInteraction(`NavigationMenu_${this.promotion.key}BannerShown`);
    }
  },
  mounted() {
    this.initPushNotifications();
    this.$nextTick(() => {
      this.$refs.menu.focus();
    });
  },
  methods: {
    ...mapActions('login', ['logout']),
    ...mapActions('modalRootStore', ['openModal']),
    ...mapActions('planAhead', ['addPlan']),
    ...mapActions('pushNotifications', ['initPushNotifications']),
    ...mapMutations('dailyRoutines', ['setShowDailyRoutineViewer']),
    openFaq() {
      Segment.trackUserInteraction('NavigationMenu_HelpCenterClicked');
      window.open('https://intercom.riseup.co.il/he?utm_source=help-center&utm_medium=riseup-menue', '_blank');
      this.closeModal();
    },
    goToFacebook() {
      Segment.trackUserInteraction('NavigationMenu_FacebookGroup');
      window.open('https://www.facebook.com/groups/riseuptogethergroup', '_blank');
      this.closeModal();
    },
    goToAmbassadors() {
      router.push({ path: '/ambassadors' });
      Segment.trackUserInteraction('NavigationMenu_AmbassadorsClicked_FromImage');
      this.closeModal();
    },
    goToEoy() {
      router.push({ path: '/st/eoy2024' });
      this.closeModal();
    },
    goToNewApp() {
      externalLinkRedirect('https://riseupil.onelink.me/SGmG/p1fqkz64');
      this.closeModal();
    },
    goToAmbassadorsFromNavigationMenu() {
      Segment.trackUserInteraction('NavigationMenu_AmbassadorsClicked');
      this.goToAmbassadors();
    },
    openChat() {
      Segment.trackUserInteraction('NavigationMenu_ChatWithTeamClicked');
      Intercom.show();
      this.closeModal();
    },
    goToPlanAheadPage() {
      Segment.trackUserInteraction('NavigationMenu_PlanAheadDashboardClicked');
      Segment.trackUserGot(
        'PlanAhead_IntroductionPopup_Test',
        { showIntro: true, hasPlans: this.hasPlans, source: 'NavigationMenu' },
      );
      if (!this.hasPlans) {
        Segment.trackUserGot('PlanAhead_IntroductionPopup_Open', { source: 'NavigationMenu' });
        this.openModal({
          component: PlanAheadIntroductionPopup,
          popupAlignment: 'full-screen',
        });
      }
      this.closeModal();
    },
    openPromotion() {
      Segment.trackUserInteraction('NavigationMenu_PromotionClicked', { promotionKey: this.promotion.key });
      this.promotion.onClick();
    },
    navigate(name) {
      router.push({ name, query: this.$route.query });
    },
    openDailyRoutine() {
      this.setShowDailyRoutineViewer(true);
      this.closeModal();
    },
    openPushNotificationPopup() {
      this.openModal({
        component: PushNotificationPermissionPopup,
        popupAlignment: 'full-screen',
      });
      this.closeModal();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@riseupil/base-ui/src/scss/riseup-colors';
@import '~@riseupil/base-ui/src/scss/spacings';
@import '~@riseupil/base-ui/src/scss/border-radius';
@import '~@riseupil/base-ui/src/scss/box-shadow';

.nav-bar {
  min-height: 100%;
  background: $riseup_beige;
  box-shadow: $ri-large-box-shadow;
  border-radius: $card-border-radius 0 0 $card-border-radius;
  padding: $ri-spacing-l $ri-spacing-ml;
  outline: none;
  color: $riseup_dark_green;

  .nav-header {
    margin-bottom: $ri-spacing-l;
  }

  .promotion-banner { // use me for new banners!
    margin-top: $ri-spacing-ml;
    height: 120px;
    img {
      cursor: pointer;
      max-width: 100%;
    }
  }

  .last-child-no-margin {
    > :last-child {
      margin-bottom: 0;
    }
  }

  .tiles-grid {
    margin-top: $ri-spacing-xl;
    display: flex;
    justify-content: space-around;
  }

  .small-circle {
    width: 24px;
    height: 24px;
    border-radius: 12px;
    background-color: $riseup_danger_red;
    color: $riseup_white;
    font-size: 14px;
    font-weight: bold;
    text-align: center;
  }
}
</style>
